<template>
  <el-header class="header">
    <div class="header-bar">
      <div class="logo-container" role="link" @click="goHome">
        <img src="@/assets/DiCloudAccessLogo.svg" alt="DiCloudAccess Logo" class="logo">
        <div class="logo-text">
          <span class="logo-text-en">DiCloudAccess</span>
          <span class="logo-text-zh">点云通</span>
        </div>
      </div>
      <!-- 修改折叠按钮 -->
      <el-button class="menu-toggle" @click="toggleMenu">
        <el-icon><Fold v-if="!showMenu" /><Expand v-else /></el-icon>
      </el-button>
    </div>
    <!-- 使用v-show来控制移动端菜单的显示 -->
    <div class="menu-overlay" v-show="showMenu" @click="toggleMenu"></div>
    <!-- PC端菜单 -->
    <el-menu mode="horizontal" class="nav-menu-desktop" :default-active="activeIndex">
      <el-menu-item index="1" @click="navigateTo('HomePage')">首页</el-menu-item>
      <el-menu-item index="2" @click="navigateTo('Features')">功能</el-menu-item>
      <el-menu-item index="3" @click="navigateTo('Pricing')">价格</el-menu-item>
      <el-dropdown @command="handleDownload">
        <el-menu-item index="4">
          下载<i class="el-icon-arrow-down el-icon--right"></i>
        </el-menu-item>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item v-for="file in availableFiles" :key="file.name" :command="file.name">
              {{ file.displayName }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <el-menu-item index="5" @click="handleSupport">支持</el-menu-item>
    </el-menu>
    <!-- 移动端菜单 -->
    <el-menu mode="vertical" class="nav-menu-mobile" :class="{ 'show': showMenu }" :default-active="activeIndex">
      <el-menu-item index="1" @click="navigateTo('HomePage')">首页</el-menu-item>
      <el-menu-item index="2" @click="navigateTo('Features')">功能</el-menu-item>
      <el-menu-item index="3" @click="navigateTo('Pricing')">价格</el-menu-item>
      <el-sub-menu index="4">
        <template #title>下载</template>
        <el-menu-item v-for="file in availableFiles" :key="file.name" @click="handleDownload(file.name)">
          {{ file.displayName }}
        </el-menu-item>
      </el-sub-menu>
      <el-menu-item index="5" @click="handleSupport">支持</el-menu-item>
    </el-menu>
    <!-- 添加对话框组件 -->
    <el-dialog
      v-model="dialogVisible"
      title="微信支持"
      width="300px"
      center
    >
      <div class="qr-code-container">
        <img src="/assets/support_qrcode.jpg" alt="微信二维码" class="qr-code">
        <p>请扫描二维码添加微信，以获取支持</p>
      </div>
    </el-dialog>
  </el-header>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import authApi from '../js/connect';
import { Fold, Expand } from '@element-plus/icons-vue'
import {
  confirmInstallPackageOnMobile,
  openInNewTab,
  resolveAssetUrl,
  resolveDownloadUrl,
  triggerDownload,
} from '../js/download';

const router = useRouter();
const route = useRoute();
const activeIndex = ref('1');
const showMenu = ref(false);

const availableFiles = ref([
  { name: 'operating_instructions.html', displayName: '在线阅读说明书', action: 'open' },
  { name: 'operating_instructions.pdf', displayName: '下载使用说明 PDF', action: 'download' },
]);

const updateInfo = ref(null);

// 获取软件更新信息
const fetchUpdateInfo = async () => {
  try {
    const response = await authApi.getUpdateInfo();
    updateInfo.value = response.data;
    
    // 更新 availableFiles 数组
    if (updateInfo.value && updateInfo.value.install_package) {
      availableFiles.value = [
        { 
          name: updateInfo.value.install_package, 
          displayName: `软件安装包`,
          action: 'download',
        },
        ...availableFiles.value // 保留原有的其他文件
      ];
    }
  } catch (error) {
    console.error('获取更新信息失败:', error);
    ElMessage.error('获取更新信息失败，请稍后重试');
  }
};

const navigateTo = (routeName) => {
  router.push({ name: routeName });
};

const goHome = () => {
  if (showMenu.value) {
    toggleMenu();
  }
  if (route.name !== 'HomePage') {
    router.push({ name: 'HomePage' });
  }
};

const toggleMenu = () => {
  showMenu.value = !showMenu.value;
  if (showMenu.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

const handleDownload = async (filename) => {
  const fileItem = availableFiles.value.find((file) => file.name === filename);

  if (fileItem?.action === 'open' || filename.endsWith('.html')) {
    openInNewTab(resolveAssetUrl(filename));
    if (showMenu.value) {
      toggleMenu();
    }
    return;
  }

  const isInstallPackage = filename === updateInfo.value?.install_package;
  if (isInstallPackage && !(await confirmInstallPackageOnMobile())) {
    return;
  }

  try {
    const isManualPdf = filename === 'operating_instructions.pdf';
    const downloadUrl = isManualPdf
      ? resolveAssetUrl(filename)
      : resolveDownloadUrl(isInstallPackage ? updateInfo.value.install_package : filename);

    triggerDownload(downloadUrl, filename);

    if (isInstallPackage) {
      ElMessageBox.alert(
        '下载已开始。下载完成后，请运行安装程序以完成软件安装。如果您在安装过程中遇到任何问题，请联系我们的支持团队。',
        '正在下载',
        {
          confirmButtonText: '确定',
          type: 'success',
        }
      );
    } else {
      ElMessage.success('文件下载已开始');
    }
  } catch (error) {
    console.error('下载失败:', error);
    ElMessage.error(`下载失败: ${error.message}`);
  }
};

// 添加对话框可见性状态
const dialogVisible = ref(false);

// 修改 handleSupport 函数
const handleSupport = () => {
  dialogVisible.value = true;
};

// 根据当前路由设置活动菜单项
const setActiveIndex = () => {
  if (route.name === 'HomePage') {
    activeIndex.value = '1';
  } else if (route.name === 'Features') {
    activeIndex.value = '2';
  } else if (route.name === 'Pricing') {
    activeIndex.value = '3';
  }
};

// 监听路由变化
watch(() => route.name, setActiveIndex);

// 在组件挂载时设置活动菜单项
onMounted(() => {
  setActiveIndex();
  fetchUpdateInfo();
});

// 在组件卸载时恢复body的overflow
onUnmounted(() => {
  document.body.style.overflow = '';
});
</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* 背景通栏，内容居中约束到 1200px，与下方分区对齐 */
  padding: 10px max(24px, calc((100% - 1200px) / 2));
  background-color: var(--dca-surface);
  border-bottom: 1px solid var(--dca-border);
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  height: 70px;
  position: relative;
}

.header-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 0 1 auto;
  min-width: 0;
}

.logo-container {
  display: flex;
  align-items: center;
  height: 100%;
  position: static;
  left: auto;
  top: auto;
  flex-shrink: 0;
  cursor: pointer;
}

.logo {
  height: 50px;
  margin-right: 10px;
}

.logo-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
}

.logo-text-en {
  font-size: 1.5em;
  font-weight: var(--dca-fw-bold);
  color: var(--dca-text);
  line-height: 1.1;
  letter-spacing: -0.03em;
  text-align: left;
}

.logo-text-zh {
  font-size: 1.05em;
  font-weight: var(--dca-fw-medium);
  color: var(--dca-text-muted);
  line-height: 1.15;
  text-align: left;
}

.nav-menu-desktop {
  border-bottom: none;
  background-color: transparent;
  margin-left: auto;
}

.el-menu {
  --el-menu-bg-color: transparent;
  --el-menu-text-color: var(--dca-text-muted);
  --el-menu-hover-bg-color: var(--dca-surface-2);
  --el-menu-active-color: var(--dca-accent);
}

.el-menu-item {
  font-size: 15px;
  font-weight: var(--dca-fw-medium);
  color: var(--dca-text-muted);
  height: 60px;
  line-height: 60px;
  padding: 0 20px;
  letter-spacing: 0.01em;
}

.el-menu-item.is-active {
  color: var(--dca-accent);
  border-bottom-color: var(--dca-accent);
}

.menu-toggle {
  display: none;
}

.menu-overlay {
  display: none;
}

.nav-menu-mobile {
  display: none; /* 在PC端隐藏移动端菜单 */
}

/* 媒体查询，适配小屏幕 */
@media (max-width: 768px) {
  .header {
    display: block;
    padding: 0;
    height: 56px;
    position: relative;
  }

  .header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 56px;
    padding: 0 16px;
    box-sizing: border-box;
  }

  .logo-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 40px;
    position: static;
    left: auto;
    top: auto;
    flex: 0 1 auto;
    min-width: 0;
    margin: 0;
  }

  .logo {
    height: 36px;
    width: 36px;
    margin: 0 8px 0 0;
    flex-shrink: 0;
    display: block;
  }

  .logo-text {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    line-height: 1;
    gap: 2px;
  }

  .logo-text-en {
    font-size: 1.05rem;
    line-height: 1.1;
  }

  .logo-text-zh {
    font-size: 0.75rem;
    line-height: 1.1;
  }

  .nav-menu-desktop {
    display: none !important;
  }

  .menu-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: static;
    top: auto;
    right: auto;
    z-index: 1001;
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    margin: 0;
    border: none;
    border-radius: 8px;
    background-color: var(--dca-accent);
    color: var(--dca-accent-ink);
    transition: all 0.3s ease;
    padding: 0;
    flex-shrink: 0;
    box-sizing: border-box;
    line-height: 1;
  }

  .menu-toggle:hover,
  .menu-toggle:focus {
    background-color: var(--dca-accent-strong);
  }

  .menu-toggle :deep(span) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .menu-toggle :deep(.el-icon) {
    font-size: 18px;
    width: 18px;
    height: 18px;
    margin: 0;
  }

  .menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }

  .nav-menu-mobile {
    display: block;
    position: fixed;
    top: 56px;
    right: -250px;
    width: 180px;
    height: calc(100vh - 56px);
    background-color: var(--dca-surface);
    border-left: 1px solid var(--dca-border);
    transition: right 0.3s ease-in-out;
    z-index: 1000;
    overflow-y: auto;
    margin: 0;
  }

  .nav-menu-mobile.show {
    right: 0;
  }

  .el-menu-item {
    width: 100%;
    text-align: left;
  }
}

/* 添加新的样式 */
.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.qr-code {
  max-width: 200px;
  width: 100%;
  height: auto;
  margin-bottom: 15px;
}
</style>